import {
  FeatureName,
  OrderByInput,
  PermissionValues,
  SUBSCRIPTION_TIERS,
  WorkspaceFilterModel,
  cacheKeys,
  useCurrentAccount,
  useCustomMutation,
  useImpersonateWorkspace,
  useIsFeatureEnabled,
  useSetWorkspaceListOrder,
  useWorkspaceFilter,
  useWorkspaceListOrder,
} from '@gripp/shared-logic';
import {
  BaseColumnCheckbox,
  Colors,
  FilterType,
  InfiniteDataGrid,
  LoadingIndicator,
  SlideLayout,
  WorkspacesSearchInput,
  getSubscriptionTierColumn,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';
import { FilterBar } from '../../shared/components/filter/filterBar';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { EditWorkspaceMenu } from './editWorkspaceMenu';

export const Workspaces = () => {
  const isCropCircleResign = useIsFeatureEnabled(FeatureName.CropCircleRedesign);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const { impersonateWorkspace } = useImpersonateWorkspace();
  const queryClient = useQueryClient();
  const [workspacesSelected, setWorkspacesSelected] = useState<
    string[] | undefined
  >(undefined);
  const showWorkspaceMenu = useMemo(() => {
    return workspacesSelected?.length === 1 ? true : false;
  }, [workspacesSelected]);
  const updateWorkspaceSelection = (rows: any) => {
    setWorkspacesSelected(rows.map((row: any) => row.id));
  };

  const { mutateAsync: updateSubscriptionTier } = useCustomMutation({
    query: `
      mutation ChangeSubscriptionTier($input: ChangeSubscriptionTierInput!) {
        workspace {
          changeSubscriptionTier(input: $input) {
            workspaceId
          }
        }
      }`,
  });

  const scrollingOffset = isCropCircleResign ? 100 : undefined;
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(
    isCropCircleResign ? 100 : undefined, 
    isCropCircleResign ? 0 : undefined, 
    isCropCircleResign ? 0 : undefined
  );

  const { data: workspaceOrder, isLoading: orderLoading } =
    useWorkspaceListOrder();
  const { mutateAsync: setWorkspaceOrder } = useSetWorkspaceListOrder();
  const columnHelper = createColumnHelper<any>();
  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setWorkspaceOrder(orderBy);
  };
  const { data: workspaceFilter } = useWorkspaceFilter();
  const filter = useMemo(() => {
    const filter: WorkspaceFilterModel =
      workspaceFilter ?? new WorkspaceFilterModel();
    return filter.getGraphQLFilter();
  }, [workspaceFilter]);

  const checkboxColumn = columnHelper.accessor('selected', {
    cell: BaseColumnCheckbox.checkbox,
    header: BaseColumnCheckbox.checkboxHeader,
    meta: { style: styles.checkboxCell },
  });

  const allColumns = useMemo(
    () => ({
      name: columnHelper.accessor('name', {
        cell: ({ row }) => {
          const original = row.original;
          const onPress = async () => {
            await impersonateWorkspace({
              id: original.id,
              name: original.name,
              type: original.type,
            });
            window.location.href = '/activities';
          };
          return (
            <Text>
              <Button mode="text" onPress={onPress}>
                {original.name}
              </Button>
            </Text>
          );
        },
        header: () => t('workspace.list.name'),
        meta: { sortable: true },
      }),
      owner: columnHelper.accessor('owner', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.owner'),
        meta: { sortable: true },
      }),
      type: columnHelper.accessor('type', {
        cell: ({ row }) => {
          const original = row.original;
          return (
            <Text>
              {original.type ? t(`workspace.types.${original.type}`) : ''}
            </Text>
          );
        },
        header: () => t('workspace.list.type'),
        meta: { sortable: true },
      }),
      subscriptionTierId: columnHelper.accessor('subscriptionTierId', {
        cell: (info) =>
          getSubscriptionTierColumn(
            info,
            SUBSCRIPTION_TIERS,
            async ({ workspaceId, tierId }) => {
              await updateSubscriptionTier({
                input: {
                  workspaceId,
                  tierId,
                },
              });
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('workspace').all,
              });
            }
          ),
        header: () => t('workspace.filter.columns.subscriptionTierId'),
        meta: { sortable: true },
      }),
      assetsCount: columnHelper.accessor('assetsCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.assetsCount'),
        meta: { sortable: true },
      }),
      routinesCount: columnHelper.accessor('routinesCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.routinesCount'),
        meta: { sortable: true },
      }),
      alerts: columnHelper.accessor('alerts', {
        cell: (info) => <Text>{info.getValue() || 0}</Text>,
        header: () => t('workspace.list.alerts'),
        meta: { sortable: true },
      }),
      peopleCount: columnHelper.accessor('peopleCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.peopleCount'),
        meta: { sortable: true },
      }),
      activitiesCount: columnHelper.accessor('activitiesCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.activitiesCount'),
        meta: { sortable: true },
      }),
      groupsCount: columnHelper.accessor('groupsCount', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.groupsCount'),
        meta: { sortable: true },
      }),
      createdAt: columnHelper.accessor('createdAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.createdAt'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      createdDate: columnHelper.accessor('createdDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.createdDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.createdDate'),
          exportCell: (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      createdTime: columnHelper.accessor('createdTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.createdTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.createdTime'),
          exportCell: (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
      createdBy: columnHelper.accessor('createdBy', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('workspace.list.createdBy'),
        meta: { sortable: true },
      }),
      lastScanned: columnHelper.accessor('lastScanned', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.lastScanned'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      lastScannedDate: columnHelper.accessor('lastScannedDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastScannedDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastScannedDate'),
          exportCell: (data: any) => {
            if (!data.lastScanned) return '';
            return new Date(data.lastScanned).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      lastScannedTime: columnHelper.accessor('lastScannedTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastScannedTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastScannedTime'),
          exportCell: (data: any) => {
            if (!data.lastScanned) return '';
            return new Date(data.lastScanned)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
      updatedAt: columnHelper.accessor('updatedAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.updatedAt'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      updatedDate: columnHelper.accessor('updatedDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.updatedDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.updatedDate'),
          exportCell: (data: any) => {
            if (!data.updatedAt) return '';
            return new Date(data.updatedAt).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      updatedTime: columnHelper.accessor('updatedTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.updatedTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.updatedTime'),
          exportCell: (data: any) => {
            if (!data.updatedAt) return '';
            return new Date(data.updatedAt)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
      lastActivity: columnHelper.accessor('lastActivity', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('workspace.list.lastActivity'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export (use split date/time columns instead)
        },
      }),
      lastActivityDate: columnHelper.accessor('lastActivityDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastActivityDate'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastActivityDate'),
          exportCell: (data: any) => {
            if (!data.lastActivity) return '';
            return new Date(data.lastActivity).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      lastActivityTime: columnHelper.accessor('lastActivityTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('workspace.list.lastActivityTime'),
        meta: {
          sortable: false,
          exportHeader: t('workspace.list.lastActivityTime'),
          exportCell: (data: any) => {
            if (!data.lastActivity) return '';
            return new Date(data.lastActivity)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
    }),
    [t, navigate, impersonateWorkspace, columnHelper]
  );

  const { columns, exportColumns } = useMemo(() => {
    // Define the correct column order
    const columnOrder: (keyof typeof allColumns)[] = [
      'owner',
      'type',
      'subscriptionTierId',
      'assetsCount',
      'routinesCount',
      'alerts',
      'peopleCount',
      'activitiesCount',
      'groupsCount',
      'createdAt',
      'lastScanned',
      'updatedAt',
      'lastActivity',
    ];

    // Build columns in the correct order
    const columnList: (typeof allColumns)[keyof typeof allColumns][] = [];
    if (workspaceFilter?.columns) {
      // Add columns in the defined order, only if they're in the filter
      columnOrder.forEach((key) => {
        if (workspaceFilter.columns.includes(key) && allColumns[key]) {
          columnList.push(allColumns[key]);
        }
      });
    } else {
      // If no filter, use default order
      columnOrder.forEach((key) => {
        if (allColumns[key]) {
          columnList.push(allColumns[key]);
        }
      });
    }

    // Always include export-only columns for data export
    const exportOnlyColumns = [
      allColumns.createdDate,
      allColumns.createdTime,
      allColumns.lastScannedDate,
      allColumns.lastScannedTime,
      allColumns.updatedDate,
      allColumns.updatedTime,
      allColumns.lastActivityDate,
      allColumns.lastActivityTime,
    ];

    // Full set for export (includes hidden/export-only)
    const allColumnsForExport = [
      checkboxColumn,
      allColumns.name,
      ...columnList,
      ...exportOnlyColumns,
    ];

    // Filter out hidden columns from grid display but keep them for export
    const gridColumns = allColumnsForExport.filter(
      (column) => !(column.meta as any)?.hidden
    );

    return {
      columns: gridColumns,
      exportColumns: allColumnsForExport,
    };
  }, [workspaceFilter?.columns, allColumns, checkboxColumn]);

  const RenderEditWorkspaceMenu = () =>
    showWorkspaceMenu && workspacesSelected ? (
      <EditWorkspaceMenu
        workspaceId={workspacesSelected[0]}
        label={t('workspace.menu.editWorkspace')}
      />
    ) : (
      <></>
    );

  useEffect(() => {
    if (!account) return;

    if (!account.hasPermission(PermissionValues.globalAdmin)) {
      navigate(`/accounts/${account.workspace?.id}`);
    }
  }, [account, navigate]);

  // Wait for orderBy to load from storage before rendering grid to prevent double query
  if (orderLoading) {
    return <LoadingIndicator />;
  }

  const headerComponent = (
    <View style={styles.filtersContainer}>
      <View style={styles.searchContainer}>
        <WorkspacesSearchInput />
      </View>
      <FilterBar
        filterButtonTypes={[
          FilterType.WORKSPACETIERS,
          FilterType.WORKSPACECOLUMNS,
        ]}
      />
    </View>
  );

  const columnComponent = (
    <View>
      <View
        style={[
          styles.workspacesContainer,
          {
            maxHeight: containerMaxHeight,
          },
        ]}
      >
        <InfiniteDataGrid
          columns={columns}
          exportColumns={exportColumns}
          query={GET_WORKSPACES}
          dataPrefix="workspace.listWorkspaces"
          modelName="workspace"
          filter={filter}
          orderBy={workspaceOrder}
          pageSize={50}
          hideHeader={false}
          listMaxHeight={listMaxHeight}
          showScrollIndicator={true}
          onOrderByChange={onOrderByChange}
          updateRowsSelected={(rows) => updateWorkspaceSelection(rows)}
          headerContent={RenderEditWorkspaceMenu()}
          exportFileName="workspaces"
        />
      </View>
    </View>
  );

  return (
    <SlideLayout
      leftColumn={{
        title: 'admin.menu.workspaces',
        mainComponent: columnComponent,
        headerComponent: headerComponent,
        disableScroll: true,
      }}
    />
  );
};

const GET_WORKSPACES = `
  query GetWorkspaces($filter: ListWorkspacesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    workspace {
      listWorkspaces(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        offset
        limit
        items {
          id
          name
          type
          owner
          subscriptionTierId
          assetsCount
          routinesCount
          alerts
          peopleCount
          activitiesCount
          groupsCount
          createdAt
          createdBy
          lastScanned
          updatedAt
          lastActivity
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  workspacesContainer: {
    flex: 1,
    borderRadius: 4,
    overflow: 'hidden',
  },
  export: {
    flexDirection: 'row',
    margin: 5,
  },
  exportAllAccountsContainer: {
    flexDirection: 'column',
    alignSelf: 'flex-end',
  },
  editColumn: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    width: 30,
    maxWidth: 30,
    marginLeft: 50,
  },
  editModal: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 4,
    backgroundColor: Colors.blackInactive,
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
});
